import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
} from '@chakra-ui/react';
import {TablerDotsVertical, TablerUserX, TablerRefresh} from '../../icons';
import DetailBackButton from './DetailBackButton';

// Shared chrome for a drilled-in encounter detail: a top-left back button and
// a patient-link header (title + context subline + link/unlink action), with
// the detail body below. Used by both the regular unlinked encounter and the
// company phone encounter so the "Unlinked Patient" / "Link Patient" treatment
// is identical across surfaces.
const EncounterDetailFrame = ({
  children,
  onBack,
  backLabel = 'Back to encounters',
  backTestId,
  showHeader = true,
  patientName = null,
  unlinkedTitle = 'Unlinked Patient',
  unlinkedSubline = 'Not linked to a patient record',
  linkedSubline = 'Linked patient',
  onLinkPatient = null,
  onChangePatient = null,
  onUnlinkPatient = null,
  linkLabel = 'Link Patient',
  // When the surrounding column is full-bleed (scrollbar flush to the edge),
  // the header sits outside the scroller and would otherwise hug the edge, so
  // it takes a matching right inset to stay aligned with the body content.
  headerPr = 0,
}: Record<string, any>) => {
  const isLinked = Boolean(patientName);
  const hasLinkedActions = Boolean(onChangePatient || onUnlinkPatient);

  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column">
      <Box
        as="header"
        pb={4}
        pr={headerPr}
        flexShrink={0}
        data-testid="encounter-detail-header"
      >
        {onBack ? (
          <DetailBackButton
            label={backLabel}
            onClick={onBack}
            testId={backTestId}
            mb={3}
          />
        ) : null}
        {showHeader ? (
          <Flex align="flex-start" justify="space-between" gap={4} minW={0}>
            <Box minW={0}>
              <Text
                as="h1"
                fontSize={{base: 'xl', md: '2xl'}}
                lineHeight="1.2"
                fontWeight="bold"
                sx={{fontWeight: '700 !important'}}
              >
                {patientName || unlinkedTitle}
              </Text>
              <Text fontSize="md" lineHeight="1.4" mt={1} fontWeight="normal">
                {isLinked ? linkedSubline : unlinkedSubline}
              </Text>
            </Box>
            <HStack
              spacing={2}
              flexShrink={0}
              flexWrap="wrap"
              justify="flex-end"
            >
              {isLinked && hasLinkedActions ? (
                <Menu placement="bottom-end">
                  <MenuButton
                    as={IconButton}
                    type="button"
                    aria-label="Patient actions"
                    icon={<TablerDotsVertical size={18} aria-hidden="true" />}
                    variant="ghost"
                    minW="44px"
                    minH="44px"
                  />
                  <MenuList minW="12rem">
                    {onChangePatient ? (
                      <MenuItem
                        icon={<TablerRefresh size={16} aria-hidden="true" />}
                        onClick={onChangePatient}
                      >
                        Change patient
                      </MenuItem>
                    ) : null}
                    {onUnlinkPatient ? (
                      <MenuItem
                        icon={<TablerUserX size={16} aria-hidden="true" />}
                        onClick={onUnlinkPatient}
                      >
                        Unlink patient
                      </MenuItem>
                    ) : null}
                  </MenuList>
                </Menu>
              ) : !isLinked && onLinkPatient ? (
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  whiteSpace="nowrap"
                  onClick={onLinkPatient}
                >
                  {linkLabel}
                </Button>
              ) : null}
            </HStack>
          </Flex>
        ) : null}
      </Box>
      <Box flex={1} minH={0}>
        {children}
      </Box>
    </Box>
  );
};

export default EncounterDetailFrame;
